import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Slider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { collection, query, where, onSnapshot, doc, updateDoc, Timestamp, addDoc, deleteDoc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { useObras } from '../../context/ObrasContext';
import { Herramienta } from '../../types';
import HerramientaKanban from './HerramientaKanban';
import HerramientaList from './HerramientaList';
import AddHerramientaModal from './AddHerramientaModal';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { ToggleButton, ToggleButtonGroup, useMediaQuery, useTheme } from '@mui/material';

interface HerramientasViewProps {
    // onAddClick is deprecated as we handle it internally now
}

const HerramientasView: React.FC<HerramientasViewProps> = () => {
    const { usuario } = useAuth();
    const { obras } = useObras();
    const [herramientas, setHerramientas] = useState<Herramienta[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [viewMode, setViewMode] = useState<'kanban' | 'list'>('kanban');

    // Modal state for Add/Edit
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTool, setEditingTool] = useState<Herramienta | null>(null);

    // States for Quantity Transfer
    const [transferDialogOpen, setTransferDialogOpen] = useState(false);
    const [toolToTransfer, setToolToTransfer] = useState<Herramienta | null>(null);
    const [transferTargetObraId, setTransferTargetObraId] = useState<string | null>(null);
    const [transferQty, setTransferQty] = useState(1);
    const [transferError, setTransferError] = useState<string | null>(null);

    const puedeEditar = usuario && (usuario.role === 'ADMIN' || usuario.role === 'EDITOR');

    useEffect(() => {
        if (isMobile) {
            setViewMode('list');
        } else {
            setViewMode('kanban');
        }
    }, [isMobile]);

    useEffect(() => {
        if (!usuario) return;

        // Fetch all active tools (en_uso) including those in Oficina (no obraId) and in Obras
        const q = query(
            collection(db, 'herramientas'),
            where('estado', '==', 'en_uso')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const toolsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Herramienta[];

            // Filter logic:
            // User can see tools if:
            // 1. They are ADMIN/Editor
            // 2. Or the tool is in a project they are assigned to
            // 3. Or the tool is in Oficina (maybe everyone sees office tools? or only assigned?) 
            //    Let's assume everyone can see Oficina tools for now to be safe, or check permissions.

            const permittedTools = toolsData.filter(tool => {
                if (usuario.role === 'ADMIN' || usuario.puedeVerTodasLasObras) return true;

                // If tool is in a project, check assignment
                if (tool.obraId && usuario.obrasAsignadas.includes(tool.obraId)) return true;

                // If tool is in Office (no obraId), maybe allow visibility? 
                // Or maybe restricted? Let's allow visibility of Office tools so they can drag them to their projects.
                if (!tool.obraId) return true;

                return false;
            });

            // Sort by name for consistent order before Kanban grouping
            permittedTools.sort((a, b) => a.nombre.localeCompare(b.nombre));

            setHerramientas(permittedTools);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching herramientas:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [usuario]);

    const handleToolMove = async (toolId: string, targetObraId: string | null) => {
        const tool = herramientas.find(t => t.id === toolId);
        if (!tool) return;

        // If moving to same location, do nothing
        if ((!targetObraId && !tool.obraId) || (targetObraId === tool.obraId)) {
            return;
        }

        const maxQty = tool.cantidad || 1;

        if (maxQty > 1) {
            // Open the quantity transfer dialog
            setToolToTransfer(tool);
            setTransferTargetObraId(targetObraId);
            setTransferQty(maxQty); // Default to moving all
            setTransferError(null);
            setTransferDialogOpen(true);
        } else {
            // Move 1 unit immediately
            await executeToolMove(tool, targetObraId, 1);
        }
    };

    const executeToolMove = async (tool: Herramienta, targetObraId: string | null, qtyToMove: number) => {
        try {
            const toolDocRef = doc(db, 'herramientas', tool.id);
            const totalQuantity = tool.cantidad || 1;

            if (qtyToMove <= 0 || qtyToMove > totalQuantity) {
                alert("Cantidad inválida");
                return;
            }

            // Buscar si ya existe una herramienta idéntica en el destino
            const targetQuery = query(
                collection(db, 'herramientas'),
                where('nombre', '==', tool.nombre),
                where('tipo', '==', tool.tipo || 'nuestra'),
                where('obraId', '==', targetObraId || '')
            );
            
            const querySnapshot = await getDocs(targetQuery);
            const existingDestTool = querySnapshot.docs.find(d => {
                const dData = d.data();
                // En la web todas las activas tienen estado 'en_uso'
                return (dData.estado || 'en_uso') === 'en_uso';
            });

            const isMovingAll = qtyToMove === totalQuantity;

            let targetObraNombre = "";
            if (targetObraId) {
                const targetObra = obras.find(o => o.id === targetObraId);
                targetObraNombre = targetObra ? targetObra.nombre : 'Desconocida';
            } else {
                targetObraNombre = "Oficina";
            }

            if (existingDestTool) {
                // 1. Si existe, sumamos la cantidad en el destino
                const destToolRef = doc(db, 'herramientas', existingDestTool.id);
                const currentDestQty = existingDestTool.data().cantidad || 1;
                
                const destUpdates: any = {
                    cantidad: currentDestQty + qtyToMove,
                    fechaAsignacion: Timestamp.now(),
                    usuarioEmail: usuario?.email || 'unknown',
                };
                
                if (!targetObraId) {
                    destUpdates.fechaDevolucion = Timestamp.now();
                } else {
                    destUpdates.fechaDevolucion = null;
                }
                
                await updateDoc(destToolRef, destUpdates);

                // 2. Actualizamos el origen
                if (isMovingAll) {
                    await deleteDoc(toolDocRef);
                } else {
                    await updateDoc(toolDocRef, { cantidad: totalQuantity - qtyToMove });
                }
            } else {
                // Si no existe en el destino
                if (isMovingAll) {
                    // Si movemos todo, simplemente cambiamos de obra
                    const updates: any = {
                        obraId: targetObraId || "",
                        obraNombre: targetObraNombre,
                        fechaAsignacion: Timestamp.now(),
                        usuarioEmail: usuario?.email || 'unknown',
                    };
                    if (!targetObraId) {
                        updates.fechaDevolucion = Timestamp.now();
                    } else {
                        updates.fechaDevolucion = null;
                    }
                    await updateDoc(toolDocRef, updates);
                } else {
                    // Si movemos algunas, restamos en el origen y creamos un nuevo doc en el destino
                    await updateDoc(toolDocRef, { cantidad: totalQuantity - qtyToMove });

                    const nuevaHerramienta: Omit<Herramienta, 'id'> = {
                        nombre: tool.nombre,
                        descripcion: tool.descripcion || '',
                        obraId: targetObraId || "",
                        obraNombre: targetObraNombre,
                        fechaAsignacion: Timestamp.now(),
                        asignadaPor: tool.asignadaPor || (usuario?.email || 'unknown'),
                        usuarioEmail: usuario?.email || undefined,
                        estado: 'en_uso',
                        tipo: tool.tipo || 'nuestra',
                        cantidad: qtyToMove
                    };

                    if (!targetObraId) {
                        nuevaHerramienta.fechaDevolucion = Timestamp.now();
                    }

                    if (tool.tipo === 'alquilada') {
                        nuevaHerramienta.fechaInicioAlquiler = tool.fechaInicioAlquiler || null;
                        nuevaHerramienta.duracionAlquilerDias = tool.duracionAlquilerDias || undefined;
                    }

                    await addDoc(collection(db, 'herramientas'), nuevaHerramienta);
                }
            }
        } catch (error) {
            console.error("Error updating tool location:", error);
            alert("Error al mover la herramienta");
        }
    };

    const handleConfirmTransfer = async () => {
        if (!toolToTransfer) return;
        if (transferQty <= 0 || transferQty > (toolToTransfer.cantidad || 1)) {
            setTransferError("Cantidad inválida");
            return;
        }
        await executeToolMove(toolToTransfer, transferTargetObraId, transferQty);
        setTransferDialogOpen(false);
        setToolToTransfer(null);
        setTransferTargetObraId(null);
    };

    const handleEdit = (tool: Herramienta) => {
        setEditingTool(tool);
        setIsModalOpen(true);
    };

    const handleDelete = async (toolId: string) => {
        if (window.confirm("¿Seguro que deseas eliminar esta herramienta permanentemente?")) {
            try {
                await deleteDoc(doc(db, 'herramientas', toolId));
            } catch (error) {
                console.error("Error deleting tool:", error);
                alert("Error al eliminar la herramienta");
            }
        }
    };

    const handleNewToolClick = () => {
        setEditingTool(null);
        setIsModalOpen(true);
    };

    const filteredTools = herramientas.filter(tool =>
        tool.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (tool.obraNombre && tool.obraNombre.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (loading) {
        return <Typography>Cargando herramientas...</Typography>;
    }

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        placeholder="Buscar herramienta u obra..."
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: { xs: '100%', sm: 300 } }}
                    />

                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(e, newView) => {
                            if (newView !== null) {
                                setViewMode(newView);
                            }
                        }}
                        size="small"
                    >
                        <ToggleButton value="kanban" aria-label="kanban view">
                            <ViewModuleIcon />
                        </ToggleButton>
                        <ToggleButton value="list" aria-label="list view">
                            <ViewListIcon />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {puedeEditar && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleNewToolClick}
                        fullWidth={isMobile}
                    >
                        Nueva Herramienta
                    </Button>
                )}
            </Box>

            {viewMode === 'kanban' ? (
                <HerramientaKanban
                    herramientas={filteredTools}
                    obras={obras}
                    onToolMove={handleToolMove}
                    onEdit={puedeEditar ? handleEdit : undefined}
                    onDelete={puedeEditar ? handleDelete : undefined}
                />
            ) : (
                <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                    <HerramientaList
                        herramientas={filteredTools}
                        obras={obras}
                        onToolMove={handleToolMove}
                        onEdit={puedeEditar ? handleEdit : undefined}
                        onDelete={puedeEditar ? handleDelete : undefined}
                    />
                </Box>
            )}

            <AddHerramientaModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSuccess={() => {}}
                herramientaToEdit={editingTool}
            />

            {/* Dialog to select transfer quantity */}
            <Dialog open={transferDialogOpen} onClose={() => setTransferDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Mover Herramienta</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1.5 }}>
                        {transferError && <Typography color="error">{transferError}</Typography>}
                        <Typography variant="body1">
                            ¿Cuántas unidades de <strong>{toolToTransfer?.nombre}</strong> deseas mover?
                        </Typography>
                        
                        <Box sx={{ px: 2, mt: 2 }}>
                            <Slider
                                value={transferQty}
                                min={1}
                                max={toolToTransfer?.cantidad || 1}
                                step={1}
                                marks
                                valueLabelDisplay="on"
                                onChange={(e, val) => setTransferQty(val as number)}
                            />
                        </Box>
                        
                        <TextField
                            label="Cantidad a mover"
                            type="number"
                            size="small"
                            value={transferQty}
                            onChange={(e) => {
                                const val = parseInt(e.target.value) || 1;
                                setTransferQty(Math.min(val, toolToTransfer?.cantidad || 1));
                            }}
                            inputProps={{ min: 1, max: toolToTransfer?.cantidad || 1 }}
                            fullWidth
                            sx={{ mt: 1 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={() => setTransferDialogOpen(false)} color="inherit">
                        Cancelar
                    </Button>
                    <Button onClick={handleConfirmTransfer} variant="contained">
                        Mover
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default HerramientasView;
